import { EMAIL_FORMAT_MESSAGE } from "../../../utils/fieldMessages";
import {
  buildStaffErrorFallback,
  getUserFacingErrorText,
  STAFF_SUPPORT_EMAIL,
  UserFacingError,
} from "../../../utils/userFacingError";

export const maxNewsletterTestRecipientCount = 5;

export type NewsletterTestSendPreview = {
  normalizedRecipientEmails: string[];
  fromEmail: string;
  replyToEmail?: string | null;
  explicitRecipientsAllowed: boolean;
  blockingReason?: string | null;
  confirmationToken?: string | null;
  confirmationExpiresAtUtcMilli?: number | null;
};

export type NewsletterTestSendResult = {
  actualRecipientEmails: string[];
};

export type PendingNewsletterTestSend = {
  clientRequestId: string;
  preview: NewsletterTestSendPreview;
};

export type NewsletterTestSendConfirmationAttempt =
  | {
      sent: true;
      pending: null;
      error: "";
    }
  | {
      sent: false;
      pending: PendingNewsletterTestSend;
      error: string;
    };

const isValidEmailAddress = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

export const normalizeNewsletterTestRecipientInput = (
  value: string
): string[] => {
  const normalizedRecipients = Array.from(
    new Set(
      value
        .split(",")
        .map((address) => address.trim().toLowerCase())
        .filter(Boolean)
    )
  );

  if (normalizedRecipients.length === 0) {
    throw new UserFacingError("Enter at least one email address.");
  }

  if (normalizedRecipients.length > maxNewsletterTestRecipientCount) {
    throw new UserFacingError(
      `Enter no more than ${maxNewsletterTestRecipientCount} email addresses.`
    );
  }

  const invalidAddress = normalizedRecipients.find(
    (address) => address.length > 320 || !isValidEmailAddress(address)
  );
  if (invalidAddress) {
    throw new UserFacingError(
      `${invalidAddress} is not a full email address. ${EMAIL_FORMAT_MESSAGE}`
    );
  }

  return normalizedRecipients;
};

/**
 * The same identifier is sent to preview and mutation and retained for retry.
 * This allows the backend to make a double-click or network retry idempotent.
 */
export const createNewsletterTestClientRequestId = (): string => {
  if (
    typeof globalThis.crypto === "undefined" ||
    typeof globalThis.crypto.getRandomValues !== "function"
  ) {
    throw new UserFacingError(
      "Your browser could not start the test email. Refresh the page and try again. If it keeps happening, use the latest version of Chrome, Edge, Safari or Firefox."
    );
  }

  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (value) =>
    value.toString(16).padStart(2, "0")
  ).join("");

  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join("-");
};

export const prepareNewsletterTestSendConfirmation = async ({
  rawRecipientInput,
  previewTestSend,
  createClientRequestId = createNewsletterTestClientRequestId,
}: {
  rawRecipientInput: string;
  previewTestSend: (
    recipientEmails: string[],
    clientRequestId: string
  ) => Promise<NewsletterTestSendPreview>;
  createClientRequestId?: () => string;
}): Promise<PendingNewsletterTestSend> => {
  const recipientEmails =
    normalizeNewsletterTestRecipientInput(rawRecipientInput);
  const clientRequestId = createClientRequestId();
  const preview = await previewTestSend(recipientEmails, clientRequestId);

  if (!preview.explicitRecipientsAllowed) {
    // blockingReason is server text for staff and is shown as it is.
    throw new UserFacingError(
      preview.blockingReason ||
        `Test emails cannot be sent to these addresses for your chamber. To turn on test emails, email ${STAFF_SUPPORT_EMAIL}.`
    );
  }
  if (
    !preview.confirmationToken ||
    !preview.confirmationExpiresAtUtcMilli
  ) {
    throw new Error(
      "The server did not return a usable test-email confirmation. Try again."
    );
  }

  return { clientRequestId, preview };
};

export const confirmPreparedNewsletterTestSend = async ({
  pending,
  sendTestEmail,
}: {
  pending: PendingNewsletterTestSend;
  sendTestEmail: (
    recipientEmails: string[],
    clientRequestId: string,
    confirmationToken: string
  ) => Promise<void>;
}): Promise<NewsletterTestSendConfirmationAttempt> => {
  try {
    await sendTestEmail(
      pending.preview.normalizedRecipientEmails,
      pending.clientRequestId,
      pending.preview.confirmationToken!
    );
    return { sent: true, pending: null, error: "" };
  } catch (error) {
    return {
      sent: false,
      pending,
      error: getUserFacingErrorText(
        error,
        buildStaffErrorFallback(
          "send the test email",
          "Check the email addresses."
        )
      ),
    };
  }
};
